import { BaseElement, BaseElementOptions } from "../display/BaseElement.js";
import { Marker, MarkerUpdateInfo } from "../display/Marker.js";

export interface Canvas3dOptions extends BaseElementOptions {
    radius: number;
}

export class Canvas3d extends BaseElement<HTMLDivElement>{

    public readonly onUpdate = e5.Signal.create();

    public readonly foreground: HTMLDivElement;
    public readonly scene: THREE.Scene;
    public readonly renderer: THREE.WebGLRenderer;
    public readonly camera: THREE.PerspectiveCamera;
    public readonly canvas: HTMLCanvasElement;

    private _marker: HTMLDivElement;
    private _radius: number = 0;
    private _markers: Marker[] = [];
    private _raycaster: THREE.Raycaster = new THREE.Raycaster();

    public create(options: Canvas3dOptions): this {
        super.create(options);
        this.setElement(this.createElement());

        this._radius = options.radius;

        var width = this.element.offsetWidth;
        var height = this.element.offsetHeight;

        this._mutable.camera = new THREE.PerspectiveCamera(70, width / height, 895, 1000);
        this.camera.position.z = 1000;
        this.camera.zoom = 1;

        this._mutable.scene = new THREE.Scene();

        this._mutable.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, logarithmicDepthBuffer: false });
        this.renderer.setClearAlpha(1);
        this.renderer.setClearColor("#003e6e");
        if (window.devicePixelRatio) {
            this.renderer.setPixelRatio(window.devicePixelRatio);
        }

        this.renderer.domElement.classList.add("e5-canvas3d__canvas");
        // this.renderer.setSize(width, height);

        this.renderer.setAnimationLoop((time: number) => this.animation(time));
        this.element.appendChild(this.renderer.domElement);
        this._mutable.canvas = this.renderer.domElement;

        this._mutable.foreground = document.createElement("div");
        this.foreground.className = "e5-canvas3d__foreground";
        this.element.appendChild(this.foreground);

        this._marker = document.createElement("div");
        this._marker.className = "e5-canvas3d__marker";
        this.element.appendChild(this._marker);

        this.onUpdate.add(() => this.updateForeground());
        this.onUpdate.add(() => this.updateMarkers());

        this.resize();

        // this.canvas.style.opacity = "0";
        // setTimeout(() => {
        //     this.canvas.style.opacity = "1";
        // }, 500);

        return this;
    }

    protected createElement(): HTMLDivElement {
        var e = document.createElement("div");
        e.className = "awi-canvas3d";
        return e;
    }

    public addMarker(marker: Marker): void {
        this._marker.appendChild(marker.element);
        this._markers.push(marker);
    }

    private updateForeground(): void {
        var size = this.computeProjectedRadius(this._radius) * 2;
        this.foreground.style.width = size + "px";
        this.foreground.style.height = size + "px";
    }

    private updateMarkers(): void {
        var info: MarkerUpdateInfo = {
            cameraDistance: this.camera.position.length(),
            x: 0,
            y: 0,
            zoom: this.camera.zoom
        }

        /**
         * Sort by camera distance.
         */
        for (var m of this._markers) {
            m.cameraDistance = m.position.distanceTo(this.camera.position);
        }
        this._markers.sort((a, b) => {
            return b.cameraDistance - a.cameraDistance;
        });

        var zIndex = 1;
        for (var m of this._markers) {
            m.element.style.zIndex = e5.Calc.clamp(m.zIndexOffset + zIndex++, 0, 8192) + "";
            var pos = m.position.clone().project(this.camera);
            // pos.x /= this.renderer.getPixelRatio();
            // pos.y /= this.renderer.getPixelRatio();
            info.x = (pos.x + 1) * this.canvas.width * 0.5;
            info.y = (-pos.y + 1) * this.canvas.height * 0.5;
            info.x /= this.renderer.getPixelRatio();
            info.y /= this.renderer.getPixelRatio();
            m.update(info);
        }
    }

    public local3dToGlobal2d(v: THREE.Vector3): THREE.Vector2 {

        var width = this.renderer.domElement.offsetWidth;
        var height = this.renderer.domElement.offsetHeight;
        var widthHalf = width / 2;
        var heightHalf = height / 2;

        v = v.clone();
        v.project(this.camera);

        return new THREE.Vector2(
            (v.x * widthHalf) + widthHalf,
            - (v.y * heightHalf) + heightHalf)
    }

    public getVector(clientX: number, clientY: number): THREE.Vector3 {
        var client = this.clampSpherical(clientX, clientY);
        var hit = this.hitSphere(client.x, client.y, 500);
        return hit;
    }

    private clampSpherical(clientX: number, clientY: number): THREE.Vector2 {
        var b = this.canvas.getBoundingClientRect();

        clientX -= b.x;
        clientY -= b.y;

        var cx = b.width * 0.5;
        var cy = b.height * 0.5;

        var rad = this.computeProjectedRadius();


        var px = clientX - cx;
        var py = clientY - cy;

        var rx = e5.Calc.clamp(px / rad, -1, 1);
        var ry = e5.Calc.clamp(py / rad, -1, 1);

        var len = Math.sqrt(rx * rx + ry * ry);
        var maxLen = 0.99;
        if (len > maxLen) {
            var m = maxLen / len;
            rx *= m;
            ry *= m;
            len = maxLen;
        }

        // rad -= 10;
        clientX = (rx * rad) + cx;
        clientY = (ry * rad) + cy;

        clientX += b.x;
        clientY += b.y;

        return new THREE.Vector2(clientX, clientY);
    }

    public computeProjectedRadius(radius3d: number = this._radius, cameraDistance: number = this.camera.position.length()) {
        var fovy = (this.camera as any).fov || 0;

        if (cameraDistance < radius3d) {
            console.warn("Camera distance is shorter than the radius of the sphere.");
            cameraDistance = radius3d + 1;
        }
        var fov = fovy / 2 * Math.PI / 180.0;
        var result = 1.0 / Math.tan(fov) * radius3d / Math.sqrt(cameraDistance * cameraDistance - radius3d * radius3d);
        var k = result * this.renderer.domElement.offsetHeight * 0.5;

        return k * this.camera.zoom;
    }

    public resize(): void {

        var width = this.element.offsetWidth;
        var height = this.element.offsetHeight;

        var size = this.renderer.getSize(new THREE.Vector2());
        if (width == size.x
            && height == size.y) {
            return;
        }

        var aspect = width / height;

        this.camera.aspect = aspect;

        var fov = 70;
        var baseWidth = 800;
        var tanFOV = Math.tan(((Math.PI / 180) * fov / 2));
        this.camera.fov = (360 / Math.PI) * Math.atan(tanFOV * (height / baseWidth));

        this.camera.updateProjectionMatrix();

        this.renderer.setSize(width, height);

        this.render();
    }

    private animation(time: number) {
        this.render();
    }

    private render(): void {
        this.camera.updateMatrixWorld();
        this.onUpdate.dispatch();
        this.renderer.render(this.scene, this.camera);
    }

    public toGlobal(position: THREE.Vector3): { x: number; y: number } {
        var pos = position.clone();
        pos.project(this.camera);
        return { x: (pos.x + 1) * this.canvas.width * 0.5, y: (-pos.y + 1) * this.canvas.height * 0.5 };
    }

    public lngLatToVector(lng: number, lat: number, radius: number = this._radius, result: THREE.Vector3 = new THREE.Vector3()): THREE.Vector3 {
        var lambda = (lng + 180) * Math.PI / 180;
        var phi = lat * Math.PI / 180;
        var cosPhi = Math.cos(phi);
        result.x = radius * -cosPhi * Math.cos(lambda);
        result.y = radius * Math.sin(phi);
        result.z = radius * cosPhi * Math.sin(lambda);
        return result;
    }

    public vectorToLatLng(v: THREE.Vector3, result: THREE.Vector2 = new THREE.Vector2()): THREE.Vector2 {
        var radius = v.length();//Math.sqrt(kX * kX + kY * kY + kZ * kZ);
        var latitude = Math.asin(v.y / radius) * 180 / Math.PI;
        var longitude = -Math.atan2(v.z, v.x) * 180 / Math.PI;
        result.x = longitude;
        result.y = latitude;
        return result;
    }


    public hitTest(clientX: number, clientY: number, elements: THREE.Object3D[], recursive: boolean = false): THREE.Intersection[] {
        var local = this.toLocal(clientX, clientY);
        var x = local.x;
        var y = local.y;
        //Transform to NDC (Normal Device Coordinates).
        x = (x / this.canvas.width) * 2 - 1;
        y = - (y / this.canvas.height) * 2 + 1;
        this._raycaster.setFromCamera({ x: x, y: y }, this.camera);
        var intersects = this._raycaster.intersectObjects(elements, recursive);
        if (intersects.length > 0)
            return intersects;
        return null;
    }

    private _sphere = new THREE.Sphere(new THREE.Vector3(), 1);

    public hitSphere(clientX: number, clientY: number, radius: number = this._radius): THREE.Vector3 {
        var local = this.toLocal(clientX, clientY);
        var x = local.x;
        var y = local.y;
        //Transform to NDC (Normal Device Coordinates).
        x = (x / this.canvas.width) * 2 - 1;
        y = - (y / this.canvas.height) * 2 + 1;
        this._raycaster.setFromCamera({ x: x, y: y }, this.camera);


        this._sphere.radius = radius;
        var t = new THREE.Vector3();
        var inter = this._raycaster.ray.intersectSphere(this._sphere, t);
        // var intersects = this._raycaster.intersectObjects(elements, recursive);
        // if (intersects.length > 0)
        //     return intersects;
        return inter;
    }

    private toLocal(clientX: number, clientY: number): { x: number; y: number } {
        var bounds = this.canvas.getBoundingClientRect();
        return { x: clientX - bounds.left, y: clientY - bounds.top };
    }
}